import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import Swal from "sweetalert2";
import AdminNavbar from "./AdminNavbar";

const API_URL = process.env.REACT_APP_API_URL;
const TINYMCE_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

export default function EditProject() {
  const { name } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", description: "", date: "", content: "" });
  const [existingThumbnail, setExistingThumbnail] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/projects/detail/${encodeURIComponent(name)}`)
      .then((r) => r.json())
      .then((data) => {
        setForm({
          name: data.name || "",
          description: data.description || "",
          date: data.date ? data.date.substring(0, 10) : "",
          content: data.content || "",
        });
        setExistingThumbnail(data.thumbnail || "");
      })
      .catch(() => {
        Swal.fire({ icon: "error", title: "Failed to load project", confirmButtonColor: "#111111" });
        navigate("/ProjectsDashboard");
      })
      .finally(() => setLoading(false));
  }, [name, navigate]);

  const handleChange = (e) => setForm({ ...form, [e.target.id]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const content = editorRef.current ? editorRef.current.getContent() : form.content;

    const data = new FormData();
    data.append("name", form.name);
    data.append("description", form.description);
    data.append("date", form.date);
    data.append("content", content);
    if (file) data.append("thumbnail", file);

    try {
      const res = await fetch(
        `${API_URL}/api/projects/${encodeURIComponent(name)}`,
        { method: "PUT", body: data },
      );
      if (!res.ok) throw new Error("Failed");
      await Swal.fire({
        icon: "success",
        title: "Updated.",
        text: "Project saved successfully.",
        confirmButtonColor: "#111111",
      });
      navigate("/ProjectsDashboard");
    } catch {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: "Please try again.",
        confirmButtonColor: "#111111",
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-shell">
        <AdminNavbar />
        <div className="admin-page">
          <div className="loader-wrap">
            <div className="loader" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <AdminNavbar />

      <div className="admin-page">
        <div className="admin-head">
          <div>
            <p className="eyebrow">Edit</p>
            <h1>Update <em>project</em>.</h1>
          </div>
        </div>

        <form className="admin-form wide" onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="name">Project Name</label>
            <input
              id="name"
              type="text"
              placeholder="Project title"
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="description">Short Description</label>
            <textarea
              id="description"
              rows="3"
              placeholder="One-line summary..."
              value={form.description}
              onChange={handleChange}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="date">Date</label>
            <input
              id="date"
              type="date"
              value={form.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="field">
            <label>Thumbnail</label>
            {existingThumbnail && !file && (
              <div style={{ marginBottom: "0.75rem" }}>
                <img
                  src={existingThumbnail}
                  alt="Current thumbnail"
                  style={{ height: 80, borderRadius: 4, objectFit: "cover" }}
                />
                <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.4rem" }}>
                  Current thumbnail — choose a new file to replace it.
                </p>
              </div>
            )}
            <label className="file-drop">
              {file
                ? <><strong>{file.name}</strong> — click to replace</>
                : <>Drop or <strong>choose an image</strong> to replace</>}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>

          <div className="field">
            <label>Case Study / Body</label>
            <div className="editor-wrap">
              <Editor
                apiKey={TINYMCE_KEY}
                onInit={(_, editor) => (editorRef.current = editor)}
                initialValue={form.content}
                init={{
                  height: 780,
                  min_height: 600,
                  menubar: "file edit view insert format tools",
                  plugins:
                    "link image lists code table quickbars autolink fullscreen media searchreplace preview wordcount",
                  toolbar:
                    "undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media blockquote | table | code preview | fullscreen",
                  resize: true,
                  branding: false,
                  statusbar: true,
                  elementpath: false,
                  images_upload_url: `${API_URL}/api/upload/inline`,
                  automatic_uploads: true,
                  paste_data_images: true,
                  images_reuse_filename: true,
                  content_style: `
                    body {
                      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                      font-size: 17px;
                      line-height: 1.8;
                      color: #0a0a0a;
                      padding: 1.5rem clamp(1rem, 5vw, 3rem);
                      max-width: 900px;
                      margin: 0 auto;
                    }
                    p { margin: 0 0 1.4rem; }
                    h1, h2, h3, h4 {
                      font-family: 'Inter', -apple-system, sans-serif;
                      font-weight: 700;
                      letter-spacing: -0.02em;
                      line-height: 1.2;
                      color: #0a0a0a;
                      margin: 2.4rem 0 1rem;
                    }
                    h1 { font-size: 2.4rem; }
                    h2 { font-size: 2rem; }
                    h3 { font-size: 1.55rem; }
                    blockquote {
                      border-left: 3px solid #111111;
                      background: #fafafa;
                      padding: 1rem 1.5rem;
                      margin: 2rem 0;
                      font-style: italic;
                      font-size: 1.2rem;
                      color: #2a2a2a;
                      border-radius: 0 4px 4px 0;
                    }
                    a { color: #111111; border-bottom: 1px solid #111111; }
                    img {
                      display: block !important;
                      margin: 2rem auto !important;
                      max-width: 100% !important;
                      height: auto !important;
                      border-radius: 4px;
                      float: none !important;
                    }
                    figure { margin: 2rem auto; text-align: center; }
                    figure img { margin: 0 auto !important; }
                    figcaption {
                      font-style: italic;
                      color: #6b6b6b;
                      font-size: 0.95rem;
                      margin-top: 0.75rem;
                    }
                    ul, ol { padding-left: 1.5rem; margin: 0 0 1.4rem; }
                    li { margin-bottom: 0.5rem; }
                    li::marker { color: #111111; }
                    hr { border: 0; height: 1px; background: #ececec; margin: 2rem auto; width: 40%; }
                    code {
                      background: #fafafa; border: 1px solid #ececec;
                      padding: 2px 6px; border-radius: 3px;
                      color: #111111; font-size: 0.92em;
                    }
                    pre {
                      background: #0a0a0a; color: #fff;
                      padding: 1rem 1.25rem; border-radius: 4px; overflow-x: auto;
                    }
                    pre code { background: transparent; color: inherit; border: none; padding: 0; }
                  `,
                }}
              />
            </div>
          </div>

          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? "Saving..." : "Save Changes →"}
          </button>
        </form>
      </div>
    </div>
  );
}
